/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';
import moment from 'moment';

import React, { Component } from 'react';
import { getTid } from 'utils/tid';
import {
	FlexLayout,
	FlexItem,
	Container,
	SingleDatepicker as DatePicker,
	TimePicker,
} from '@waca/ca-ui-toolkit';
import DateTimeFilter from './DateTimeFilter';

export default class DateTimePane extends Component<any, any> {
	static propTypes = {
		inputDate: PropTypes.string,
		inputTime: PropTypes.string,
		dataType: PropTypes.string,
		onDateChange: PropTypes.func,
		onTimeChange: PropTypes.func,
		locale: PropTypes.string.isRequired,
		ariaLabelDate: PropTypes.string,
		ariaLabelTime: PropTypes.string,
		dateOnly: PropTypes.bool,
		readOnly: PropTypes.bool,
	};

	static defaultProps = {
		inputDate: '',
		inputTime: '',
		dataType: 'datetime',
		dateOnly: false,
		readOnly: false,
	};

	state = {
		time: moment(this.props.inputTime, DateTimeFilter.DISPLAY_TIME_FORMAT),
		date: moment(this.props.inputDate, DateTimeFilter.ISO_DATE_FORMAT),
	};

	componentWillReceiveProps(nextProps) {
		if (this.props.inputTime !== nextProps.inputTime) {
			this.setState({
				time: moment(nextProps.inputTime, DateTimeFilter.DISPLAY_TIME_FORMAT),
			});
		}
		if (this.props.inputDate !== nextProps.inputDate) {
			this.setState({ date: moment(nextProps.inputDate, DateTimeFilter.ISO_DATE_FORMAT) });
		}
	}

	public getDate = () => {
		const { date } = this.state;

		if (date && date.isValid()) {
			date.format(DateTimeFilter.ISO_DATE_FORMAT);
		}

		return null;
	};

	public getTime = () => {
		const { time } = this.state;

		if (time && time.isValid()) {
			time.format(DateTimeFilter.ISO_TIME_FORMAT);
		}

		return null;
	};

	_onDateChange = date => {
		this.setState({ date });

		if (date && date.isValid()) {
			this.props.onDateChange(date.format(DateTimeFilter.ISO_DATE_FORMAT));
		}
	};

	_onTimeChange = time => {
		this.setState({ time });

		if (time && time.isValid()) {
			this.props.onTimeChange(time.format(DateTimeFilter.ISO_TIME_FORMAT));
		}
	};

	_revertDate(date) {
		this.setState({ date: moment(date, DateTimeFilter.ISO_DATE_FORMAT) });
	}

	_revertTime(time) {
		this.setState({ time: moment(time, DateTimeFilter.DISPLAY_TIME_FORMAT) });
	}

	_renderDate = () => {
		const { locale, readOnly } = this.props;
		const { date } = this.state;

		return (
			<Container data-tid={getTid('dateTimePane', 'datePickerSection')}>
				<DatePicker
					locale={locale}
					date={date}
					onChange={this._onDateChange}
					disabled={readOnly}
					numberOfMonths={1}
				/>
			</Container>
		);
	};

	_renderTime = () => {
		const { locale, readOnly } = this.props;
		const { time } = this.state;

		return (
			<Container data-tid={getTid('dateTimePane', 'timePickerSection')}>
				<TimePicker
					locale={locale}
					time={time}
					onChange={this._onTimeChange}
					disabled={readOnly}
					showSec
				/>
			</Container>
		);
	};

	_renderDateTime = () => {
		const { locale, ariaLabelDate, ariaLabelTime, dateOnly, readOnly } = this.props;

		const { date, time } = this.state;

		return (
			<FlexLayout direction="row" data-tid={getTid('dateTimePane', 'dateTimePickerSection')}>
				<FlexItem
					data-tid={getTid('dateTimePane', 'datePickerSection')}
					gutter={[0, 1, 0, 0]}
				>
					<DatePicker
						locale={locale}
						date={date}
						onChange={this._onDateChange}
						ariaLabel={ariaLabelDate}
						disabled={readOnly}
						numberOfMonths={1}
					/>
				</FlexItem>
				<FlexItem data-tid={getTid('dateTimePane', 'timePickerSection')}>
					<TimePicker
						locale={locale}
						time={time}
						onChange={this._onTimeChange}
						ariaLabel={ariaLabelTime}
						disabled={readOnly || dateOnly}
						showSec
					/>
				</FlexItem>
			</FlexLayout>
		);
	};

	render() {
		switch (this.props.dataType) {
			case 'datetime':
				return this._renderDateTime();
			case 'date':
				return this._renderDate();
			case 'time':
				return this._renderTime();
			default:
				return this._renderDateTime();
		}
	}
}
